"use client";

import React, { createContext, useContext } from "react";
import type { BaseEntity } from "../../UniversalCRMTable/types";
import type {
  PageConfig,
  AuthState,
  DataState as DataStateType,
  EntityPageNavigation,
  EntityState,
  FilterState,
  ExportState,
  Handlers,
  Callbacks,
  BulkActions,
  SavedViewsState,
  ViewModeState,
  InfiniteScrollState,
  PaginationModeContextValue,
} from "./types";
import type { UseMutationResult } from "@tanstack/react-query";

/**
 * Consolidated context for UniversalEntityPage
 * Eliminates prop drilling by providing all page state and handlers via context
 */

export interface UniversalEntityPageContextValue<T extends BaseEntity> {
  // Page configuration (static, from props)
  pageConfig: PageConfig<T>;

  // Auth and UI state
  auth: AuthState;

  // Navigation
  navigation: EntityPageNavigation;

  // Data and loading state
  dataState: DataStateType;

  // Entity state (editing, deleting, etc.)
  entityState: EntityState<T>;

  // Filter state
  filterState: FilterState<T>;

  // Export state
  exportState: ExportState;

  // Event handlers
  handlers: Handlers<T>;

  // Memoized callbacks and render functions
  callbacks: Callbacks<T>;

  // Mutations
  mutations: {
    deleteMutation: { isPending: boolean };
  };

  // Bulk Actions
  bulkActions: BulkActions;

  // Bulk action mutations passed from page level
  bulkActionMutations?: {
    emailExport?: UseMutationResult<unknown, Error, unknown>;
  };

  // Saved Views State
  savedViewsState: SavedViewsState;

  // View Mode State
  viewModeState: ViewModeState;

  // Infinite Scroll State (DEPRECATED - use paginationModeState instead)
  infiniteScrollState: InfiniteScrollState;

  // Unified Pagination Mode State (NEW - replaces paginationState + infiniteScrollState)
  paginationModeState: PaginationModeContextValue;
}

const UniversalEntityPageContext = createContext<UniversalEntityPageContextValue<any> | undefined>(
  undefined
);

export { UniversalEntityPageContext };

interface UniversalEntityPageContextProviderProps<T extends BaseEntity> {
  value: UniversalEntityPageContextValue<T>;
  children: React.ReactNode;
}

/**
 * Provider component for UniversalEntityPageContext
 */
export function UniversalEntityPageContextProvider<T extends BaseEntity>({
  value,
  children,
}: UniversalEntityPageContextProviderProps<T>) {
  return (
    <UniversalEntityPageContext.Provider value={value}>
      {children}
    </UniversalEntityPageContext.Provider>
  );
}

/**
 * Custom hook to access UniversalEntityPageContext
 * Throws error if used outside of provider
 */
export function useOptionalUniversalEntityPageContext<
  T extends BaseEntity = BaseEntity,
>(): UniversalEntityPageContextValue<T> | null {
  const context = useContext(UniversalEntityPageContext);
  return (context as UniversalEntityPageContextValue<T> | undefined) ?? null;
}

export function useUniversalEntityPageContext<
  T extends BaseEntity = BaseEntity,
>(): UniversalEntityPageContextValue<T> {
  const context = useContext(UniversalEntityPageContext);
  if (!context) {
    throw new Error(
      "useUniversalEntityPageContext must be used within UniversalEntityPageContextProvider"
    );
  }
  return context as UniversalEntityPageContextValue<T>;
}
